import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { PageHeader } from "../../components/common/PageHeader";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { VectorStoreList } from "../../components/documents/VectorStoreList";
import { VectorStoreDialog } from "../../components/documents/VectorStoreDialog";
import { FileUpload } from "../../components/documents/FileUpload";
import { FileList } from "../../components/documents/FileList";
import { SearchTest } from "../../components/documents/SearchTest";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListVectorStores,
  useCreateVectorStore,
  useUpdateVectorStore,
  useDeleteVectorStore,
  useListFiles,
  useDeleteFile,
  useReindexFile,
  useListTeams,
  useListMembers,
  getListVectorStoresQueryKey,
  getListFilesQueryKey,
} from "../../api/generated/generated";
import type { VectorStoreResponse as VectorStore } from "../../api/generated";
import { useUploadFile } from "../../hooks/useVectorStores";
import { useAuth } from "../../contexts/AuthContext";
import type {
  ScopeOption,
  VectorStoreSubmit,
} from "../../components/documents/VectorStoreDialog";

export function DocumentsPage() {
  const [selectedStore, setSelectedStore] = useState<VectorStore | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<VectorStore | null>(null);
  const [deleteStore, setDeleteStore] = useState<VectorStore | null>(null);

  const { user } = useAuth();
  const orgId = user?.orgId ?? "";
  const isOwner =
    (user?.roles?.includes("org_owner") ?? false) || (user?.superAdmin ?? false);

  const queryClient = useQueryClient();
  const teamsQuery = useListTeams(orgId, { query: { enabled: !!orgId } });
  const membersQuery = useListMembers(orgId, { query: { enabled: !!orgId } });
  const stores = useListVectorStores();
  const createStore = useCreateVectorStore();
  const updateStore = useUpdateVectorStore();
  const deleteStoreMutation = useDeleteVectorStore();
  const files = useListFiles(selectedStore?.id ?? "", {
    query: {
      enabled: !!selectedStore?.id,
      refetchInterval: (query) => {
        const resp = query.state.data;
        if (resp?.status !== 200) return false;
        const hasProcessing = resp.data.some(
          (f) => f.status === "pending" || f.status === "processing",
        );
        return hasProcessing ? 5000 : false;
      },
    },
  });
  const uploadFile = useUploadFile(selectedStore?.id ?? "");
  const deleteFile = useDeleteFile();
  const reindexFile = useReindexFile();

  // Scopes the current user may create a KB at: Organization (owner/super-admin) plus any team
  // they own/manage. Reps without a manageable scope only get personal KBs (spec 040).
  const allTeams = teamsQuery.data?.status === 200 ? teamsQuery.data.data : [];
  const myManagedTeamIds = isOwner
    ? allTeams.map((t) => t.id)
    : ((membersQuery.data?.status === 200 ? membersQuery.data.data : []).find(
        (m) => m.uid === user?.uid,
      )?.managed_team_ids ?? []);
  const scopeOptions: ScopeOption[] = [
    ...(isOwner
      ? [{ label: "Organization", scope: "org" as const, scopeOwnerId: null }]
      : []),
    ...allTeams
      .filter((t) => myManagedTeamIds.includes(t.id))
      .map((t) => ({
        label: `Team: ${t.name}`,
        scope: "team" as const,
        scopeOwnerId: t.id,
      })),
  ];

  const handleCreateOrUpdate = (data: VectorStoreSubmit) => {
    if (editingStore) {
      updateStore.mutate(
        {
          storeId: editingStore.id,
          data: { name: data.name, description: data.description },
        },
        {
          onSuccess: () => {
            setDialogOpen(false);
            setEditingStore(null);
          },
        },
      );
    } else {
      createStore.mutate(
        {
          data: {
            name: data.name,
            description: data.description,
            scope: data.scope,
            scope_owner_id: data.scopeOwnerId,
          },
        },
        {
          onSuccess: () => {
            setDialogOpen(false);
            queryClient.invalidateQueries({
              queryKey: getListVectorStoresQueryKey(),
            });
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteStore) return;
    deleteStoreMutation.mutate(
      { storeId: deleteStore.id },
      {
        onSuccess: () => {
          setDeleteStore(null);
          if (selectedStore?.id === deleteStore.id) {
            setSelectedStore(null);
          }
        },
      },
    );
  };

  if (stores.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (stores.isError) {
    return <Alert severity="error">Failed to load knowledge bases.</Alert>;
  }

  // Detail view for a selected vector store
  if (selectedStore) {
    // Capture as a non-null const so callbacks below don't need `!` assertions.
    const store = selectedStore;
    return (
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton onClick={() => setSelectedStore(null)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{selectedStore.name}</Typography>
        </Box>
        {selectedStore.description && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            {selectedStore.description}
          </Typography>
        )}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Upload Files
          </Typography>
          <FileUpload
            onUpload={(file) => uploadFile.mutate(file)}
            isUploading={uploadFile.isPending}
          />
          {uploadFile.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Upload failed. Please try again.
            </Alert>
          )}
        </Paper>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Files
          </Typography>
          {files.isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <FileList
              files={files.data?.status === 200 ? files.data.data : []}
              onDelete={(fileId) =>
                deleteFile.mutate(
                  { storeId: store.id, fileId },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries({
                        queryKey: getListFilesQueryKey(store.id),
                      });
                    },
                  },
                )
              }
              onReindex={(fileId) =>
                reindexFile.mutate({ storeId: store.id, fileId })
              }
            />
          )}
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Test Search
          </Typography>
          <SearchTest storeId={selectedStore.id} />
        </Paper>
      </Box>
    );
  }

  // List view
  return (
    <Box>
      <PageHeader
        title="Documents"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingStore(null);
              setDialogOpen(true);
            }}
          >
            New Knowledge Base
          </Button>
        }
      />

      <VectorStoreList
        stores={stores.data?.data ?? []}
        onSelect={setSelectedStore}
        onEdit={(store) => {
          setEditingStore(store);
          setDialogOpen(true);
        }}
        onDelete={setDeleteStore}
      />

      {(stores.data?.data?.length ?? 0) > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Search All Knowledge Bases
            </Typography>
            <SearchTest />
          </Paper>
        </>
      )}

      <VectorStoreDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingStore(null);
        }}
        onSubmit={handleCreateOrUpdate}
        store={editingStore}
        isLoading={createStore.isPending || updateStore.isPending}
        scopeOptions={scopeOptions}
      />

      <Dialog open={Boolean(deleteStore)} onClose={() => setDeleteStore(null)}>
        <DialogTitle>Delete Knowledge Base</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{deleteStore?.name}"? This will
            permanently remove all files, chunks, and embeddings. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteStore(null)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteStoreMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
